const TRANSCRIPT_DRAFT_DB = "akuna-transcript-drafts";
const TRANSCRIPT_DRAFT_VERSION = 1;
const DRAFT_STORE = "drafts";

export type TranscriptDraft = {
  transcriptionId: string;
  transcript: string;
  updatedAt: string;
};

function isIndexedDbAvailable() {
  return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
}

function openDraftDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(TRANSCRIPT_DRAFT_DB, TRANSCRIPT_DRAFT_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DRAFT_STORE)) {
        db.createObjectStore(DRAFT_STORE, { keyPath: "transcriptionId" });
      }
    };
  });
}

async function withDb<T>(handler: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openDraftDb();
  try {
    return await handler(db);
  } finally {
    db.close();
  }
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveTranscriptDraft(transcriptionId: string, transcript: string): Promise<void> {
  if (!isIndexedDbAvailable() || !transcriptionId) return;

  try {
    await withDb(async (db) => {
      const transaction = db.transaction(DRAFT_STORE, "readwrite");
      transaction.objectStore(DRAFT_STORE).put({
        transcriptionId,
        transcript,
        updatedAt: new Date().toISOString(),
      } satisfies TranscriptDraft);
      await transactionDone(transaction);
    });
  } catch (error) {
    // Rascunho local e melhor-esforco: nunca deve interromper a gravacao.
    console.warn("[transcription] draft:saveFailed", error);
  }
}

export async function loadTranscriptDraft(transcriptionId: string): Promise<TranscriptDraft | null> {
  if (!isIndexedDbAvailable() || !transcriptionId) return null;

  try {
    return await withDb(async (db) => {
      const transaction = db.transaction(DRAFT_STORE, "readonly");
      const result = await requestToPromise(
        transaction.objectStore(DRAFT_STORE).get(transcriptionId) as IDBRequest<TranscriptDraft | undefined>,
      );
      await transactionDone(transaction);
      return result ?? null;
    });
  } catch (error) {
    console.warn("[transcription] draft:loadFailed", error);
    return null;
  }
}

export async function clearTranscriptDraft(transcriptionId: string): Promise<void> {
  if (!isIndexedDbAvailable() || !transcriptionId) return;

  try {
    await withDb(async (db) => {
      const transaction = db.transaction(DRAFT_STORE, "readwrite");
      transaction.objectStore(DRAFT_STORE).delete(transcriptionId);
      await transactionDone(transaction);
    });
  } catch (error) {
    console.warn("[transcription] draft:clearFailed", error);
  }
}
